import { Button } from '@c4ads/c4blocks';
import FilterListIcon from '@mui/icons-material/FilterList';
import { GridFilterModel, GridSortModel, useGridApiRef } from '@mui/x-data-grid-premium';
import { DataGridPremiumProps } from '@mui/x-data-grid-premium/models/dataGridPremiumProps';
import { useImperativeHandle, useState } from 'react';
import { styled } from '@mui/material/styles';
import { DataGridPremium, gridClasses } from '@mui/x-data-grid-premium';
import cn from 'classnames';
import styles from './CustomDataGrid.module.css';

const DataGridComponent = styled(DataGridPremium)(({ theme }) => ({
    borderLeft: 'none',
    borderRight: 'none',
    borderRadius: 0,
    '& .MuiDataGrid-columnHeaders': {
        width: '100%',
        borderRadius: 0,
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
        '& .MuiButtonBase-root': {
            color: theme.palette.common.white,
        },
    },
    '& .MuiDataGrid-pinnedColumnHeaders': {
        backgroundColor: theme.palette.primary.dark,
    },
    '& .MuiDataGrid-cell': {
        color: 'primary',
        fontWeight: 700,
    },
    [`& .${gridClasses.columnHeaderDraggableContainer} .${gridClasses.menuIcon}`]: {
        visibility: 'visible',
        width: 'auto',
    },
}));

interface CustomDataGridInterface extends DataGridPremiumProps {
    handleFilterModel?: (filterModel: GridFilterModel) => void;
    handleSortModel?: (sortModel: GridSortModel) => void;
    resetAll?: () => void;
}

export const CustomDataGrid = ({
    handleFilterModel,
    resetAll,
    handleSortModel,
    apiRef,
    className,
    pagination = true,
    pageSize = 25,
    rowsPerPageOptions = [25, 50, 100, 200],
    ...props
}: CustomDataGridInterface) => {
    const ref = useGridApiRef();
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    useImperativeHandle(
        apiRef,
        () => {
            return ref.current;
        },
        [ref]
    );

    const onFilterModel = (filterModel: GridFilterModel) => {
        handleFilterModel && handleFilterModel(filterModel);
        ref.current.setFilterModel(filterModel);
        setFilterModel(filterModel);
    };

    const onSortModel = (sortModel: GridSortModel) => {
        handleSortModel && handleSortModel(sortModel);
        ref.current.setSortModel(sortModel);
        setSortModel(sortModel);
    };

    const resetAllFilters = () => {
        ref.current.setFilterModel({ items: [] });
        ref.current.setSortModel([]);

        setFilterModel({ items: [] });
        setSortModel([]);

        handleSortModel && handleSortModel([]);
        handleFilterModel && handleFilterModel({ items: [] });
    };

    return (
        <div className={styles.container}>
            <div className={styles.resetAllContainer}>
                {(filterModel?.items?.length !== 0 || sortModel?.length !== 0) && (
                    <>
                        <p>{filterModel ? filterModel?.items.length : 0} Filters Applied</p>
                        <span className={styles.resetAllDivider} />
                        <p>{sortModel ? sortModel.length : 0} Sorted Column(s)</p>
                        <span className={styles.resetAllDivider} />
                        <Button className={styles.resetAllButton} color="secondary" onClick={resetAllFilters}>
                            Reset all
                        </Button>
                    </>
                )}
            </div>
            <DataGridComponent
                apiRef={ref}
                className={cn(styles.grid, className)}
                pagination={pagination}
                rowsPerPageOptions={rowsPerPageOptions}
                initialState={{
                    pagination: { pageSize },
                    ...props.initialState,
                }}
                {...props}
                components={{
                    ColumnFilteredIcon: (props) => {
                        return (
                            <div className={styles.columnFilteredIcon}>
                                <FilterListIcon fontSize={props.fondSize} className={props.className} />
                            </div>
                        );
                    },
                    ...props.components,
                }}
                onFilterModelChange={onFilterModel}
                onSortModelChange={onSortModel}
                componentsProps={{
                    panel: {
                        sx: {
                            '& .MuiDataGrid-filterForm': {
                                alignItems: 'flex-end',
                                gap: '8px',
                            },
                            '& .MuiFormControl-root': {
                                width: 'unset',
                            },
                            ...props.sx,
                        },
                    },
                    ...props.componentsProps,
                }}
            />
        </div>
    );
};
